import cx from 'classnames'
import _ from 'lodash'
import T from 'prop-types'
import React from 'react'
import EditorInput from '../EditorInput'
import {
  ScrollBars,
  FileUploadReader
} from 'react-base'
import { IDE_PART_TYPES } from '../editorInputUtils'
import { pluralize } from 'pw-formatters'
import ImportedFileSummary from './ImportedFileSummary'
//TODO add "stealth" style options for SensorConfig integration
//TODO add "bpf" input with filtering (and maybe a smarter parser to validate in-string IPs)
//TODO add "maxCt" or similar limiting prop

const MAX_RENDERABLE_LINES = 100 // NOTE: currently only enforced on file upload
const INVALID_MSG_DELIM = ' - '

function listInputBlockStyleFn(contentBlock) {
  const { disabled, invalid } = contentBlock.getData().toObject()
  return cx(`list_input_line`, {
    is_disabled: disabled,
    is_invalid: invalid
  })
}

export default class extends React.Component {
  static displayName = 'ListInput'

  static propTypes = {
    allowFileUpload: T.bool,
    allowedFileExtensions: T.array,
    className: T.string,
    editorClassName: T.string,
    fileParser: T.func, // Optional parser for extracting content from uploaded files. If not supplied the inbuilt simple fileParser will be used.
    flex: T.bool, // If true, will flex to fill parent and the input and invalidSummary will independently scroll
    focusOnMount: T.bool,
    initialValue: T.string,
    lineClassName: T.string, // Not used if custom parser is supplied
    lineLabel: T.string,
    lineValidator: T.func, // Not used if custom parser is supplied
    lineValidatorArgs: T.array,
    onChange: T.func,
    onChangeArgs: T.array,
    parser: T.func, // Optional line/editorBlock parser. If not supplied the inbuilt simple parser will be used
    parserOptions: T.object,
    placeholder: T.string,
    allowDuplicates: T.bool, // If false, will validate against duplicate entries
    readOnly: T.bool,
    restrictSingle: T.bool,
    onImport: T.func, // Called with import result message
    lineSanitizer: T.func,
    monospace: T.bool, // If true, use monospace styling
    typeaheadOptions: T.arrayOf(T.string),
    // Delimiter used for simple typeahead management, when no custom `parser` is supplied.
    // Falsy/default behavior uses newlines as delimiters (whole line values)
    typeaheadDelimiter: T.string,
    maxLines: T.number // Max number of lines to allow
  }

  static defaultProps = {
    allowFileUpload: false,
    editorClassName: '',
    flex: false,
    focusOnMount: false,
    hasFocus: false,
    lineClassName: 'basic_list_line',
    lineLabel: 'line',
    lineValidator: _.noop,
    lineValidatorArgs: [],
    onChangeArgs: [],
    parserOptions: {},
    placeholder: 'Start typing...',
    allowDuplicates: false,
    restrictSingle: false, // restrict to only a single line
    monospace: true
  }

  constructor(props) {
    super(props)
    this.state = {
      invalidSummaries: {},
      fileImportSummary: null,
      latestRawTextBlocks: props.initialValue ? props.initialValue.split(/\n/) : []
    }
  }


  UNSAFE_componentWillMount() {
    this._debouncedStateUpdate = _.debounce(this._updateInvalidState, 300)
  }

  componentDidMount() {
    if (this.props.focusOnMount) {
      this.focus()
    }
  }

  componentWillUnmount() {
    this._isUnmounting = true
  }

  setValue = (newValue = '') => {
    if (this._editor) {
      this._editor.setValue(newValue)
    }
  }

  resetToInitialValue = () => {
    this.setState({
      fileImportSummary: null
    })
    this.setValue(this.props.initialValue || '')
  }

  focus = () => {
    if (this._editor) {
      this._editor.focus()
    }
  }

  triggerChange = () => {
    if (this._editor) {
      this._editor.triggerChange()
    }
  }

  _updateInvalidState = (rawTextBlocks, blockData = []) => {
    if (this._isUnmounting) {
      return
    }

    const invalidSummaries = blockData.reduce(
      (out, b, i) => {
        if (b.invalidSummary && b.invalidSummary.length > 0) {
          out[`${i}`] = b.invalidSummary
        }
        return out
      },
      {}
    )

    this.setState({
      latestRawTextBlocks: rawTextBlocks,
      invalidSummaries
    })
  }

  _handleFileData = (fileData = {}) => {
    const {
      lineLabel,
      onImport
    } = this.props
    // TODO same signature as EditorInput, so invalidSummary can still be used
    const numFiles = _.size(fileData)
    let rawInvalidLines = []
    const rawValidLines = _.reduce(
      fileData,
      (out, fileContents) => {
        rawInvalidLines = rawInvalidLines.concat(
          _.map(fileContents.invalidLines || [], line => line.text)
        )
        return _.uniq(
          out.concat(_.map(fileContents.validLines || [], line => line.text))
        )
      },
      []
    )

    let importMessage = {
      type: rawValidLines.length > 0 ? 'success' : 'warning',
      heading: `Imported ${rawValidLines.length.toLocaleString()} valid ${pluralize(
        rawValidLines.length,
        lineLabel
      )} from ${numFiles} ${pluralize(numFiles, 'file')}`
    }

    if (rawInvalidLines.length > 0) {
      importMessage = _.assign(importMessage, {
        message:
          rawInvalidLines.length === 1
            ? `1 line was invalid or empty, and has been omitted:`
            : `${rawInvalidLines.length.toLocaleString()} lines were invalid or empty, and have been omitted:`,
        messageDetails: rawInvalidLines
      })
    }

    // CommonViewActions.Notification.add(importMessage)
    if (onImport) {
      onImport(importMessage)
    }

    // If max renderable lines exceeded, show a non-ediable simplified summary
    // of the lines uploaded
    if (rawValidLines.length > MAX_RENDERABLE_LINES) {
      // Clear editor value, now that it is hidden
      if (this._editor && rawValidLines && rawValidLines.length > 0) {
        this._editor.setValue('')
      }
      this.setState({
        fileImportSummary: fileData
      })
      this._emitChange(rawValidLines, [])
    } else {
      // If less than the max, just insert into the editor
      if (this._editor && rawValidLines && rawValidLines.length > 0) {
        this._editor.setValue(rawValidLines.join('\n'))
      }
    }
  }

  _handleListInputChange = (rawTextBlocks, textBlockData) => {
    this._emitChange(rawTextBlocks, textBlockData)
    this._debouncedStateUpdate(rawTextBlocks, textBlockData)
  }

  _emitChange = (allLines, lineData) => {
    const { onChange, onChangeArgs } = this.props
    if (onChange) {
      onChange(allLines, lineData, ...onChangeArgs)
    }
  }

  _handleFocus = () => {
    this.setState({
      hasFocus: true
    })
  }

  _handleBlur = () => {
    this.setState({
      hasFocus: false
    })
  }

  _handleEditorRef = inst => {
    this._editor = inst
  }

  getSimpleTypeaheadState  = (inputString, blockCaretOffset) => {
    const {
      typeaheadOptions,
      allowDuplicates,
      typeaheadDelimiter
    } = this.props
    const {
      latestRawTextBlocks
    } = this.state

    // If an explicit typeahead delimiter is supplied, use it for substring matching/replacement
    if (typeaheadDelimiter) {
      const inputStringSplitByDelim = inputString.split(typeaheadDelimiter)
      let runningLen = 0
      let rangeStartIdx = 0
      let rangeEndIdx = inputString.length

      inputStringSplitByDelim.forEach(inputPart => {
        const partStartIdx = runningLen
        const partEndIdx = partStartIdx + inputPart.length //, inputString.length)

        if (blockCaretOffset >= partStartIdx && blockCaretOffset <= partEndIdx) {
          rangeStartIdx = partStartIdx
          rangeEndIdx = partEndIdx
        }
        runningLen = partEndIdx + typeaheadDelimiter.length // Add to count for leading delimiter characters
      })

      return {
        typeaheadOptions: typeaheadOptions.map(optionStr => ({name: optionStr, id: optionStr})),
        matchText: inputString.slice(rangeStartIdx, blockCaretOffset),
        typeaheadRange: [rangeStartIdx, rangeEndIdx]
      }
    }

    // Default whole-line typeahead replacement
    return {
      typeaheadOptions: typeaheadOptions
        .filter(optionStr => {
          return allowDuplicates ? true : !latestRawTextBlocks.includes(optionStr)
        })
        .map(optionStr => ({name: optionStr, id: optionStr})),
      matchText: inputString.slice(0, blockCaretOffset),
      typeaheadRange: [0, inputString.length]
    }
  }

  _parseLine = (inputString = '', parserOptions, blockCaretOffset, allListInputStrings) => {
    const {
      lineValidator,
      lineValidatorArgs,
      lineSanitizer,
      lineClassName,
      readOnly,
      typeaheadOptions,
      allowDuplicates,
      maxLines
    } = this.props
    let typeaheadState = null
    const inputStringIndex = allListInputStrings.indexOf(inputString)

    if (lineSanitizer) {
      // Modify raw input text
      inputString = lineSanitizer(inputString)
    }

    const outputIDEState = [
      {
        offset: 0,
        length: inputString.length,
        text: inputString,
        partType: IDE_PART_TYPES.VALUE,
        // tooltip: "",
        className: lineClassName
      }
    ]

    // Run all parts through validation
    // TODO split this out to a separate function and call it on a debounce
    const invalidMessages = []

    if (lineValidator && !readOnly) {
      const validatorMsg = lineValidator(inputString, ...lineValidatorArgs)
      if (validatorMsg) {
        invalidMessages.push(validatorMsg)
      }
    }

    if (!allowDuplicates && inputString.length > 0 && allListInputStrings.indexOf(inputString) !== allListInputStrings.lastIndexOf(inputString)) {
      invalidMessages.push(`Duplicate values not allowed.`)
    }

    if (maxLines && (inputStringIndex + 1) > maxLines) {
      invalidMessages.push(`Maximum number of lines allowed is ${maxLines}.`)
    }

    if (typeaheadOptions) {
      typeaheadState = this.getSimpleTypeaheadState(inputString, blockCaretOffset)
    }

    if (invalidMessages.length > 0) {
      outputIDEState[0].invalidMsg = invalidMessages.join('\r\n')
    }

    return {
      IDEState: outputIDEState,
      IDEStateData: {
        invalid: invalidMessages.length > 0,
        invalidSummary: invalidMessages
      },
      newText: inputString, // Return potentially-modified raw text
      typeaheadState
    }
  }

  // Basic file parser will just insert all parsed lines into the EditorInput
  _parseFile = fileText => {
    const {
      lineValidator,
      lineValidatorArgs,
      lineSanitizer,
      allowDuplicates
    } = this.props

    const trimmedLines = _.map(fileText.split(/\n/), line => {
      if (lineSanitizer) {
        line = lineSanitizer(line)
      }
      return line.trim()
    })

    const usedLines = []
    let validLines = []
    const invalidLines = []

    if (lineValidator) {
      for (let i = 0, numLines = trimmedLines.length; i < numLines; i++) {
        const line = trimmedLines[i]
        if (line.length > 0) {
          let invalidMsg = lineValidator(line)
          if (!allowDuplicates && usedLines.includes(line)) {
            invalidMsg = invalidMsg || ''
            invalidMsg += `${invalidMsg ? ', ' : ''}Duplicate values not allowed.`
          }

          if (invalidMsg) {
            // invalidLines.push(`${ invalidMsg } - (${ line })`)
            // invalidLines.push(line)
            invalidLines.push({
              lineNumber: i,
              text: line
            })
          } else {
            // validLines.push(line)
            validLines.push({
              lineNumber: i,
              text: line
            })
          }
        } else {
          // invalidLines.push({
          //   lineNumber: i,
          //   text: ""
          // })
        }
      }
    } else {
      validLines = _.map(trimmedLines, (line, i) => {
        return {
          lineNumber: i,
          text: line
        }
      })
    }

    return {
      validLines,
      invalidLines
    }
  }

  render() {
    const { props, state } = this
    const {
      allowFileUpload,
      allowedFileExtensions,
      className,
      fileParser,
      flex,
      editorClassName,
      initialValue,
      lineLabel,
      parser,
      parserOptions,
      placeholder,
      readOnly,
      restrictSingle,
      monospace,
      typeaheadDelimiter
    } = props
    const { invalidSummaries, hasFocus, fileImportSummary } = state

    const numInvalidBlocks = _.size(invalidSummaries)

    // If no delimiter is supplied (putting ListInput into full-line replacement typeahead mode),
    // disable all of TypeaheadMenuOwner's autoselect features -- they interfere too greatly with newline-insertion (returns)
    const fullLineTypeaheadMode = !typeaheadDelimiter

    const editor = fileImportSummary
      ? <div className="imported_files">
          <header>
            Rules imported from {_.size(fileImportSummary)}{' '}
            {pluralize(_.size(fileImportSummary), 'file')}
          </header>
          {_.map(fileImportSummary, (fileData, fromFileName) =>
            <ImportedFileSummary
              fileName={fromFileName}
              key={fromFileName}
              lineLabel={lineLabel}
              {...fileData}
            />
          )}
        </div>
      : <EditorInput
          blockStyleFn={listInputBlockStyleFn}
          className={`list_input_content ${monospace ? 'monospace' : ''} ${editorClassName || ''}`}
          initialValue={initialValue}
          onBlur={this._handleBlur}
          onChange={this._handleListInputChange}
          onFocus={this._handleFocus}
          parser={parser || this._parseLine}
          parserOptions={parserOptions}
          placeholder={placeholder}
          preventReturn={restrictSingle}
          readOnly={readOnly}
          ref={this._handleEditorRef}
          omitExactMatch
          alwaysSelectFirstOption={!fullLineTypeaheadMode}
          preventAutoSelect={fullLineTypeaheadMode}
          autoTypeaheadQuotes={false}
        />

    const fileUploader =
      allowFileUpload && !readOnly
        ? <FileUploadReader
            allowedFileExtensions={allowedFileExtensions}
            className="list_input_file_upload"
            fileParser={fileParser || this._parseFile}
            onChange={this._handleFileData}
          >Upload...</FileUploadReader>
        : null

    const invalidSummaryElements = _.map(
      invalidSummaries,
      (summary, indexKey) =>
        <div className="summary_block" key={indexKey}>
          <div>
            {parseInt(indexKey) + 1}
          </div>
          <ul className="list-unstyled">
            {_.map(summary, (msg, ii) => {
              return msg.indexOf(INVALID_MSG_DELIM) === -1
                ? <li key={ii}>
                    <div>
                      {msg}
                    </div>
                  </li>
                : <li key={ii}>
                    <span>
                      {msg.split(INVALID_MSG_DELIM)[0]}
                    </span>
                    <div>
                      {msg.split(INVALID_MSG_DELIM)[1]}
                    </div>
                  </li>
            })}
          </ul>
        </div>
    )

    return (
      <div
        className={cx(`list_input_wrap`, className, {
          list_input_flex: flex,
          has_focus: hasFocus
        })}
        onClick={this.focus}
      >
        {flex
          ? <div className="rule_input_scroll_wrap">
              <ScrollBars
                classes="pw_scrollbars_light"
                horizontal
                slimShady
                slimShadyDarker
              >
                {editor}
              </ScrollBars>
              {fileUploader}
            </div>
          : <div>
              {editor}
              {fileUploader}
            </div>}
        {!readOnly && invalidSummaries && numInvalidBlocks > 0
          ? <div className="invalid_summary_wrap">
              <div className="summary_header">
                {numInvalidBlocks} Invalid{' '}
                {pluralize(numInvalidBlocks, lineLabel)}
              </div>
              {flex
                ? <div className="rule_input_scroll_wrap">
                    <ScrollBars
                      classes="pw_scrollbars_light"
                      horizontal
                      slimShady
                      slimShadyDarker
                    >
                      {invalidSummaryElements}
                    </ScrollBars>
                  </div>
                : invalidSummaryElements}
            </div>
          : null}
      </div>
    )
  }
}
